import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Box, Typography } from '@mui/joy';
import { useTheme } from '@mui/joy/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate, useParams } from 'react-router';
import { LayoutContext } from '../../context';
import {
    APIGetMockAttempt, APIGetMockResults, APIStartMockExam,
    APISaveMockAnswer, APISubmitMockBlock,
} from '../../api';
import MockExamView, { MockExamResults } from './MockExamView';

/**
 * Пробник NBME: логика попытки (таймер блока, сохранение ответов, пометки,
 * завершение блока) — здесь; внешность — MockExamView в экзаменационном скине.
 */
export default function MockExamPage() {
    const { slug = 'nbme-21' } = useParams();
    const { token } = useContext(LayoutContext);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [attempt, setAttempt] = useState(null);
    const [results, setResults] = useState(null);
    const [selected, setSelected] = useState({});
    const [marked, setMarked] = useState({});
    const [currentIndex, setCurrentIndex] = useState(0);
    const [secondsLeft, setSecondsLeft] = useState(0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const submittingRef = useRef(false);

    const loadAttempt = useCallback(async (attemptId) => {
        const data = await APIGetMockAttempt(token, attemptId);
        setAttempt(data);
        if (data.block) {
            const serverNow = Date.parse(data.server_now);
            const deadline = Date.parse(data.block.deadline_at);
            setSecondsLeft(Math.max(0, Math.floor((deadline - serverNow) / 1000)));
            const sel = {}; const mk = {};
            data.block.items.forEach((item) => {
                if (item.answer?.selected_answer_id) sel[item.id] = item.answer.selected_answer_id;
                if (item.answer?.is_marked) mk[item.id] = true;
            });
            setSelected(sel); setMarked(mk);
        }
    }, [token]);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const storageKey = `mock-exam-attempt:${slug}`;
                const savedId = window.localStorage.getItem(storageKey);
                if (savedId) {
                    try {
                        if (mounted) await loadAttempt(savedId);
                        return;
                    } catch {
                        window.localStorage.removeItem(storageKey);
                    }
                }
                const data = await APIStartMockExam(token, slug);
                window.localStorage.setItem(storageKey, String(data.id));
                if (mounted) await loadAttempt(data.id);
            } catch (err) {
                if (mounted) setError(err.response?.data?.error || 'Не удалось запустить пробник');
            }
        })();
        return () => { mounted = false; };
    }, [loadAttempt, slug, token]);

    useEffect(() => {
        if (!attempt?.block || results) return undefined;
        const timer = window.setInterval(() => setSecondsLeft((value) => Math.max(0, value - 1)), 1000);
        return () => window.clearInterval(timer);
    }, [attempt, results]);

    const items = attempt?.block?.items || [];

    const save = async (itemId, answerId, isMarked) => {
        try {
            await APISaveMockAnswer(token, attempt.id, itemId, answerId, isMarked);
            setError('');
        } catch (err) {
            setError(err.response?.data?.error || 'Ответ не сохранён');
        }
    };
    const choose = (itemId, answerId) => {
        setSelected((value) => ({ ...value, [itemId]: answerId }));
        save(itemId, answerId, !!marked[itemId]);
    };
    const toggleMark = (itemId) => {
        const next = !marked[itemId];
        setMarked((value) => ({ ...value, [itemId]: next }));
        save(itemId, selected[itemId] || null, next);
    };

    const submit = useCallback(async () => {
        if (submittingRef.current || !attempt?.block) return;
        submittingRef.current = true;
        setBusy(true);
        try {
            const next = await APISubmitMockBlock(token, attempt.id, attempt.block.index);
            if (next.status === 'finished') {
                setAttempt(next);
                setResults(await APIGetMockResults(token, attempt.id));
                window.localStorage.removeItem(`mock-exam-attempt:${slug}`);
            } else {
                setCurrentIndex(0);
                await loadAttempt(attempt.id);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Не удалось завершить блок');
        } finally {
            setBusy(false);
            submittingRef.current = false;
        }
    }, [attempt, loadAttempt, slug, token]);

    // Время блока вышло — сдаём блок сами, как на настоящем экзамене
    useEffect(() => {
        if (attempt?.block && !results && secondsLeft === 0) submit();
    }, [secondsLeft, attempt, results, submit]);

    const exit = () => navigate('/mock-exams');

    if (error && !attempt) return <Box sx={{ p: 4 }}><Alert color="danger">{error}</Alert></Box>;
    if (!attempt) return <Box sx={{ p: 4 }}><Typography>Загрузка пробника…</Typography></Box>;
    if (results) return <MockExamResults attempt={attempt} results={results} onExit={exit} />;

    return (
        <MockExamView
            attempt={attempt}
            items={items}
            blocksTotal={attempt.form?.blocks_total || 4}
            currentIndex={Math.min(currentIndex, Math.max(0, items.length - 1))}
            selected={selected}
            marked={marked}
            secondsLeft={secondsLeft}
            busy={busy}
            error={error}
            isMobile={isMobile}
            onSelect={choose}
            onNavigate={(idx) => setCurrentIndex(Math.max(0, Math.min(items.length - 1, idx)))}
            onToggleMark={toggleMark}
            onSubmitBlock={submit}
            onExit={exit}
        />
    );
}
